import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Button,
  useColorModeValue,
  Icon,
  useDisclosure,
  Modal,
  ButtonGroup,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Fade,
  Badge,
  Skeleton,
  Grid,
  GridItem,
  Wrap,
  WrapItem,
  Tooltip,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  IconButton, // Added for info button
  Link as ChakraLink, // For external links in modal
} from '@chakra-ui/react';
import { useState, useEffect, useContext } from 'react'; // Import useContext
import { useSearchParams, Link as RouterLink } from 'react-router-dom'; // Aliased Link to RouterLink
import { useSearchRefresh } from '../context/SearchRefreshContext'; // Import the context hook
import { MapPin, Funnel, MagnifyingGlass, X, TextAa, Info, SquaresFour, Columns, Rectangle } from '@phosphor-icons/react'; // Added Info icon and grid icons
import { useGeocoding } from '../hooks/useGeocoding';
import ElasticSearchBar from '../components/ElasticSearchBar';
import { Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton } from '@chakra-ui/react';
import '../styles/mobile-optimizations.css';
import ElasticSiteCard from '../components/ElasticSiteCard';
import ElasticSearchFilters from '../components/ElasticSearchFilters';
import ElasticImagePreviewModal from '../components/ElasticImagePreviewModal';
import { getMediaFeatures, generateFeatureVariations } from '../utils/featureUtils';
import { 
  searchSites, 
  transformSearchParams, 
  transformSearchResults, 
  extractFilterOptions 
} from '../utils/elasticsearchApi';

export default function SearchElasticsearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    materialType: '',
    features: [],
    era: '',
    verificationStatus: '',
    countries: [],
    // categories: [], // Removed categories filter from here
    featureFilterLogic: 'AND', 
  });
  const { isOpen: isInfoModalOpen, onOpen: onInfoModalOpen, onClose: onInfoModalClose } = useDisclosure(); // For info modal
  const { isOpen: isFilterDrawerOpen, onOpen: onFilterDrawerOpen, onClose: onFilterDrawerClose } = useDisclosure();
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedSiteMedia, setSelectedSiteMedia] = useState([]);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [featuresMetadata, setFeaturesMetadata] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  
  const location = searchParams.get('location');
  const name = searchParams.get('name');
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBgColor = useColorModeValue('white', 'gray.800');
  const badgeBg = useColorModeValue('ancient.subtle', 'gray.700');
  const badgeTextColor = useColorModeValue('gray.800', 'white');
  const [sites, setSites] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [gridColumns, setGridColumns] = useState(2); // Track grid layout
  const { reverseGeocode } = useGeocoding();

  const clearSearchTerm = (termToRemove) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(termToRemove);
    setSearchParams(newSearchParams);
  };

  // Request user's location and update search params
  /* useEffect(() => {
    if (!location && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const addressInfo = await reverseGeocode(
            position.coords.latitude,
            position.coords.longitude
          );
          
          if (addressInfo) {
            const userState = addressInfo.components.find(
              component => component.types.includes('administrative_area_level_1')
            )?.long_name;
            
            if (userState) {
              setSearchParams({ location: userState });
            }
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
        },
        { enableHighAccuracy: true }
      );
    }
  }, []); */

  // Main search effect
  const { refreshKey } = useSearchRefresh(); // Consume the refreshKey

  // Main search effect
  useEffect(() => {
    const performSearch = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Transform current search params and filters to Elasticsearch format
        const searchParameters = transformSearchParams(searchParams, filters);
        
        console.log('Elasticsearch search parameters:', searchParameters);
        console.log('Active filter features:', filters.features);
        
        // Perform the search
        const result = await searchSites(searchParameters);
        
        console.log('[SearchElasticsearch] Raw aggregations from backend:', result.aggregations); // DEBUG LOG
        
        if (result.success) {
          // Transform results back to component format
          const transformedSites = transformSearchResults(result.data);
          setSites(transformedSites);
          setTotal(result.total);
          setHasMore(result.hasMore);
          
          // Extract filter options from aggregations
          const options = extractFilterOptions(result.aggregations);
          console.log('[SearchElasticsearch] Extracted filter options:', options); // DEBUG LOG
          setFilterOptions(options);
          
          // Build features metadata for compatibility, ensuring category is included
          const featureMap = new Map();
          if (options.features) {
            options.features.forEach(feature => { // feature from options.features should have id, label, category
              featureMap.set(feature.id, { 
                id: feature.id, 
                label: feature.label, 
                category: feature.category // Include category here
              });
            });
          }
          setFeaturesMetadata(Object.fromEntries(featureMap));
          
          console.log('Search completed:', {
            sitesFound: transformedSites.length,
            total: result.total,
            aggregations: result.aggregations
          });
          
        } else {
          throw new Error(result.error || 'Search failed');
        }
      } catch (err) {
        console.error('Search error:', err);
        setError(err.message || 'An error occurred while searching');
        setSites([]);
        setTotal(0);
      }
      
      // Add a small delay for better UX
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    };

    performSearch();
  }, [location, name, filters, refreshKey]); // Add refreshKey to dependencies

  // Media handling functions
  const getFilteredMedia = (allMedia) => {
    if (!Array.isArray(allMedia)) return [];
    
    console.log('🔍 [getFilteredMedia] Starting with media:', allMedia.length);
    console.log('🔍 [getFilteredMedia] Active filters:', filters.features);
    
    // Step 1: Filter for valid image media only
    let imageMedia = allMedia.filter(media => {
      if (!media) return false;
      // Ensure it's an image and has a valid URL
      return media.type === 'image' && media.url && media.url.match(/^https?:\/\/.+/);
    });

    console.log('🔍 [getFilteredMedia] Valid image media:', imageMedia.length);

    // Step 2: If feature filters are active, further filter these images by those features
    if (filters.features && filters.features.length > 0) {
      // Create a map of normalized features to search for
      const searchFeatureVariations = new Map();
      filters.features.forEach(feature => {
        const variations = generateFeatureVariations(feature);
        console.log(`🔄 [getFilteredMedia] Generated variations for "${feature}":`, variations);
        variations.forEach(variation => {
          searchFeatureVariations.set(variation.toLowerCase(), feature);
        });
      });

      console.log('🗺️ [getFilteredMedia] Search feature variations map:', searchFeatureVariations);

      imageMedia = imageMedia.filter((media, index) => {
        // Get all features for this media item using our utility
        const mediaFeatures = getMediaFeatures(media);
        const mediaFeatureIds = new Set(mediaFeatures.map(f => f.id.toLowerCase()));

        console.log(`📷 [getFilteredMedia] Media item ${index} (${media.id}):`, {
          features: mediaFeatures,
          featureIds: Array.from(mediaFeatureIds)
        });

        // Check if media matches any of the search feature variations
        const matchingFeatures = new Set();
        for (const mediaFeatureId of mediaFeatureIds) {
          if (searchFeatureVariations.has(mediaFeatureId)) {
            matchingFeatures.add(searchFeatureVariations.get(mediaFeatureId));
            console.log(`✅ [getFilteredMedia] Match found: "${mediaFeatureId}" matches filter "${searchFeatureVariations.get(mediaFeatureId)}"`);
          }
        }

        // Apply AND/OR logic
        let matchesFilter = false;
        if (filters.featureFilterLogic === 'OR') {
          // OR: media must have at least one of the selected features
          matchesFilter = matchingFeatures.size > 0;
        } else {
          // AND: media must have all of the selected features
          matchesFilter = filters.features.every(feature => matchingFeatures.has(feature));
        }

        console.log(`🎯 [getFilteredMedia] Media item ${index} matches filter:`, matchesFilter, 'matching features:', Array.from(matchingFeatures));

        return matchesFilter;
      });
    }
    
    console.log('✅ [getFilteredMedia] Final filtered media count:', imageMedia.length);
    return imageMedia; // Return the filtered image media
  };

  const handleMediaClick = (site, mediaItem, _originalIndexFromSiteCard) => {
    // We only want to open the modal for images.
    if (mediaItem?.type !== 'image' || !mediaItem?.url?.match(/^https?:\/\/.+/)) {
      console.log('Clicked on non-image media or invalid image, not opening modal:', mediaItem);
      return;
    }

    const allImagesMatchingFilters = getFilteredMedia(site.media || []);

    if (allImagesMatchingFilters.length === 0) {
      // Site has no images, or no images match current feature filters.
      // Since an image was clicked, display it alone.
      setSelectedSiteMedia([mediaItem]);
      setSelectedMedia({ ...mediaItem, siteId: site.id });
      setSelectedMediaIndex(0);
      onOpen();
      return;
    }

    const modalIndex = allImagesMatchingFilters.findIndex(img => img.id === mediaItem.id && img.url === mediaItem.url);

    if (modalIndex === -1) {
      // Clicked image does not match current feature filters. Display it alone.
      setSelectedSiteMedia([mediaItem]);
      setSelectedMedia({ ...mediaItem, siteId: site.id });
      setSelectedMediaIndex(0);
    } else {
      // Clicked image is part of the filterable set.
      setSelectedSiteMedia(allImagesMatchingFilters);
      setSelectedMedia({ ...mediaItem, siteId: site.id });
      setSelectedMediaIndex(modalIndex);
    }
    onOpen();
  };

  const handleNavigate = (direction) => {
    if (!selectedSiteMedia || selectedSiteMedia.length === 0) return;

    let newIndex;
    if (direction === 'next') {
      newIndex = (selectedMediaIndex + 1) % selectedSiteMedia.length;
    } else {
      newIndex = (selectedMediaIndex - 1 + selectedSiteMedia.length) % selectedSiteMedia.length;
    }
    
    setSelectedMediaIndex(newIndex);
    const siteId = selectedMedia.siteId; 
    setSelectedMedia({ ...selectedSiteMedia[newIndex], siteId });
  };

  // Filter handling
  const handleFilterChange = (newFilters) => {
    // If featureFilterLogic is changing, update it directly
    if (newFilters.hasOwnProperty('featureFilterLogic')) {
      setFilters(prev => ({
        ...prev,
        featureFilterLogic: newFilters.featureFilterLogic,
      }));
    } else {
      // For other filters, merge them as before
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
    setPage(0); // Reset to first page when filters change
  };
  
  const resetFilters = () => {
    setFilters({
      materialType: '',
      features: [],
      era: '',
      verificationStatus: '',
      countries: [],
      // categories: [], // Removed categories from reset
      featureFilterLogic: 'AND', 
    });
    setPage(0);
  };

  const handleFeatureToggle = (featureOrId) => {
    // Use the original feature ID without any normalization for Elasticsearch queries
    const rawId = typeof featureOrId === 'string' ? featureOrId : featureOrId.id;
    const currentFeatures = filters.features || [];
    
    const exists = currentFeatures.includes(rawId);
    let newFeatures;
    if (exists) {
      newFeatures = currentFeatures.filter(id => id !== rawId);
    } else {
      newFeatures = [...currentFeatures, rawId];
    }
    
    setFilters(prev => ({ ...prev, features: newFeatures }));
  };

  // Calculate active filters count, excluding featureFilterLogic
  const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
    if (key === 'featureFilterLogic') return count; // Skip featureFilterLogic
    if (Array.isArray(value)) return count + value.length;
    if (value) return count + 1;
    return count;
  }, 0);

  const hasActiveFilters = activeFilterCount > 0;

  const getFeatureLabel = (featureId) => {
    return featuresMetadata[featureId]?.label || featureId;
  };

  // Loading skeletons
  const skeletonCards = Array(8).fill(0).map((_, i) => (
    <Box 
      key={`skeleton-${i}`} 
      bg="bg.surface" 
      borderRadius="xl" 
      overflow="hidden" 
      boxShadow="0 2px 4px rgba(0,0,0,0.1)"
      border="1px solid"
      borderColor="border.default"
      className="map-texture"
    >
      <Skeleton height="200px" width="100%" />
      <Box p={5}>
        <VStack align="stretch" spacing={4}>
          <Skeleton height="24px" width="70%" />
          <Skeleton height="20px" width="90%" />
          <Skeleton height="16px" width="60%" />
          <Skeleton height="16px" width="80%" />
        </VStack>
      </Box>
    </Box>
  ));

  return (
    <Box minH="100vh" bg="bg.default" className="map-texture">
      <Container maxW={{ base: "95%", md: "90%", lg: "container.xl" }} py={{ base: 2, md: 6 }}>
        <VStack spacing={{ base: 3, md: 8 }} align="stretch">
          {/* Header Section - Mobile Sticky Search */}
          <VStack 
            align="stretch" 
            spacing={{ base: 1, md: 2 }} 
            mb={{ base: 2, md: 4 }}
            position={{ base: 'sticky', md: 'static' }}
            top={{ base: 0, md: 'auto' }}
            zIndex={{ base: 10, md: 'auto' }}
            bg={{ base: 'bg.default', md: 'transparent' }}
            py={{ base: 2, md: 0 }}
            px={{ base: 2, md: 0 }}
            borderRadius={{ base: '0 0 xl xl', md: 'none' }}
            boxShadow={{ base: isLoading ? 'none' : '0 2px 8px rgba(0,0,0,0.1)', md: 'none' }}
            className={{ base: 'mobile-search-header', md: '' }}
          >
            {/* Location or name indicator */}
            {(location || name) && (
              <HStack spacing={4}>
                {location && (
                  <HStack spacing={1} maxW="100%" justifyContent="flex-start">
                    <Icon as={MapPin} color="interactive.primary" boxSize={{ base: 3, md: 4 }} />
                    <Text color="text.default" fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" noOfLines={1}>
                      {location}
                    </Text>
                    <IconButton
                      icon={<Icon as={X} />}
                      size="xs"
                      variant="ghost"
                      aria-label="Clear location search"
                      onClick={() => clearSearchTerm('location')}
                      color="text.muted"
                    />
                  </HStack>
                )}
                {name && (
                  <HStack spacing={1} maxW="100%" justifyContent="flex-start">
                    <Icon as={TextAa} color="interactive.primary" boxSize={{ base: 3, md: 4 }} />
                    <Text color="text.default" fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" noOfLines={1}>
                      {name}
                    </Text>
                    <IconButton
                      icon={<Icon as={X} />}
                      size="xs"
                      variant="ghost"
                      aria-label="Clear name search"
                      onClick={() => clearSearchTerm('name')}
                      color="text.muted"
                    />
                  </HStack>
                )}
              </HStack>
            )}
            
            {/* Search Bar */}
            <Box>
              <ElasticSearchBar 
                initialValue={location || name || ''} 
              />
            </Box>
            
            {/* Filter Controls */}
            <Flex 
              justify="flex-start" 
              align="center" 
              wrap="wrap" 
              gap={{ base: 1, md: 2 }}
            >
            </Flex>

            {/* Active Filters Display */}
            {hasActiveFilters && (
              <Box>
                <Flex justify="space-between" align="center" mb={1}>
                  <HStack spacing={1}>
                    <Icon as={Funnel} color="interactive.primary" boxSize={{ base: 2.5, md: 3 }} />
                    <Text fontSize={{ base: '2xs', md: 'xs' }} color="text.default" fontWeight="600">
                      Filters:
                    </Text>
                  </HStack>
                  <Button
                    size="xs"
                    color="interactive.primary"
                    variant="ghost"
                    onClick={resetFilters}
                    _hover={{ bg: 'bg.accent' }}
                    fontSize="2xs"
                    height="16px"
                    px={1}
                  >
                    Reset
                  </Button>
                </Flex>
                <Wrap spacing={1}>
                  {/* Display Feature Filter Logic if features are selected */}
                  {filters.features && filters.features.length > 0 && (
                    <WrapItem>
                        <Badge
                          bg={useColorModeValue('purple.100', 'purple.700')}
                          color={useColorModeValue('gray.800', 'whiteAlpha.900')}
                          borderRadius="full"
                          px={2}
                          py={1}
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight="600"
                          border="1px solid"
                          borderColor={useColorModeValue('purple.300', 'purple.500')}
                          boxShadow="0 1px 2px rgba(0,0,0,0.05)"
                          lineHeight="1.2"
                        >
                        {filters.featureFilterLogic}
                      </Badge>
                    </WrapItem>
                  )}
                  {filters.materialType && (
                    <WrapItem>
                        <Badge
                          variant="journal"
                          borderRadius="full"
                          px={2}
                          py={1}
                          display="flex"
                          alignItems="center"
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight="600"
                          boxShadow="0 1px 2px rgba(0,0,0,0.05)"
                          lineHeight="1.2"
                        >
                          <Box mr={1}>{filters.materialType}</Box>
                          <Icon 
                            as={X} 
                            cursor="pointer" 
                            boxSize={2} 
                            onClick={() => handleFilterChange({ materialType: '' })}
                            transition="all 0.2s"
                            _hover={{ transform: 'scale(1.2)' }}
                          />
                        </Badge>
                    </WrapItem>
                  )}
                  {filters.era && (
                    <WrapItem>
                        <Badge
                          bg="bg.surface"
                          color="text.default"
                          borderRadius="full"
                          px={1.5}
                          py={0.5}
                          display="flex"
                          alignItems="center"
                          fontSize={{ base: '3xs', md: '2xs' }}
                          fontWeight="500"
                          border="1px solid"
                          borderColor="border.default"
                          boxShadow="0 1px 2px rgba(0,0,0,0.05)"
                          lineHeight="1"
                        >
                          <Box mr={1}>{filters.era}</Box>
                          <Icon 
                            as={X} 
                            cursor="pointer" 
                            boxSize={2} 
                            onClick={() => handleFilterChange({ era: '' })}
                            transition="all 0.2s"
                            _hover={{ transform: 'scale(1.2)' }}
                          />
                        </Badge>
                    </WrapItem>
                  )}
                  {filters.features.map(featureId => (
                    <WrapItem key={featureId}>
                      <Tooltip label={`Remove "${getFeatureLabel(featureId)}" filter`} placement="top" hasArrow>
                        <Badge
                          bg="bg.surface"
                          color="text.default"
                          px={1.5}
                          py={0.5}
                          borderRadius="full"
                          cursor="pointer"
                          fontSize={{ base: '3xs', md: '2xs' }}
                          fontWeight="500"
                          display="flex"
                          alignItems="center"
                          onClick={() => handleFeatureToggle({ id: featureId })}
                          _hover={{ bg: 'bg.accent' }}
                          border="1px solid"
                          borderColor="border.default"
                          boxShadow="0 1px 2px rgba(0,0,0,0.05)"
                          transition="all 0.2s"
                          lineHeight="1"
                        >
                          <Box mr={1}>{getFeatureLabel(featureId)}</Box>
                          <Icon 
                            as={X} 
                            boxSize={2}
                            transition="all 0.2s"
                            _hover={{ transform: 'scale(1.2)' }}
                          />
                        </Badge>
                      </Tooltip>
                    </WrapItem>
                  ))}
                  {filters.countries?.map(country => (
                    <WrapItem key={country}>
                        <Badge
                          bg="bg.surface"
                          color="text.default"
                          borderRadius="full"
                          px={1.5}
                          py={0.5}
                          display="flex"
                          alignItems="center"
                          fontSize={{ base: '3xs', md: '2xs' }}
                          fontWeight="500"
                          border="1px solid"
                          borderColor="border.default"
                          boxShadow="0 1px 2px rgba(0,0,0,0.05)"
                          lineHeight="1"
                        >
                          <Box mr={1}>{country}</Box>
                          <Icon 
                            as={X} 
                            cursor="pointer" 
                            boxSize={2} 
                            onClick={() => {
                              const newCountries = filters.countries.filter(c => c !== country);
                              handleFilterChange({ countries: newCountries });
                            }}
                            transition="all 0.2s"
                            _hover={{ transform: 'scale(1.2)' }}
                          />
                        </Badge>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}
          </VStack>

          {/* Error Display */}
          {error && (
            <Alert status="error" borderRadius="xl">
              <AlertIcon />
              <Box>
                <AlertTitle>Search Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Main Content Grid */}
          <Grid
            templateColumns={{
              base: "1fr",
              md: isFiltersVisible ? "280px 1fr" : "1fr"
            }}
            gap={6}
          >
            {isFiltersVisible && (
              <GridItem display={{ base: 'none', md: 'block' }}>
                <Fade in={isFiltersVisible} unmountOnExit>
                  <Box
                    bg="bg.surface"
                    p={4}
                    borderRadius="xl"
                    boxShadow="0 2px 4px rgba(0,0,0,0.1)"
                    border="1px solid"
                    borderColor="border.default"
                    className="map-texture"
                  >
                    <ElasticSearchFilters
                      filters={{...filters, featuresMetadata}}
                      onFilterChange={handleFilterChange}
                      onReset={resetFilters}
                      filterOptions={filterOptions}
                      onInfoClick={onInfoModalOpen} // Pass handler to open info modal
                    />
                  </Box>
                </Fade>
              </GridItem>
            )}
            
            <GridItem>
              <VStack align="stretch" spacing={6}>
                {/* Results Info and Grid Layout Controls */}
                {!isLoading && sites.length > 0 && (
                  <Flex justify="space-between" align="center">
                    <HStack>
                    <Button
                      leftIcon={<Icon as={Funnel} boxSize={{ base: 3.5, md: 4 }} />}
                      variant="journal"
                      size="xs"
                      display={{ base: 'none', md: 'inline-flex' }}
                      fontSize={{ base: '2xs', md: 'xs' }}
                      height={{ base: '32px', md: 'auto' }}
                      fontWeight="600"
                      onClick={() => {
                        if (window.innerWidth < 768) {
                          onFilterDrawerOpen();
                        } else {
                          setIsFiltersVisible(!isFiltersVisible);
                        }
                      }}
                      px={{ base: 3, md: 4 }}
                      borderRadius="md"
                    >
                      {isFiltersVisible && window.innerWidth >= 768 ? "Hide" : "Filters"}
                      {hasActiveFilters && (
                        <Badge 
                          ml={1} 
                          colorScheme="blue" 
                          variant="solid" 
                          borderRadius="full" 
                          px={1}
                          py={0}
                          fontSize="2xs"
                          minW="16px"
                          textAlign="center"
                        >
                          {activeFilterCount}
                        </Badge>
                      )}
                    </Button>
                    {/* Results Info - positioned at top left */}
                    <HStack spacing={2} align="center">
                      <Icon as={MagnifyingGlass} color="interactive.primary" boxSize={4} />
                      <Text fontSize="sm" fontWeight="medium" color="text.default">
                        {total} {total === 1 ? 'site' : 'sites'} found
                      </Text>
                      {sites.length > 0 && total > 0 && sites.length < total && (
                        <Badge colorScheme="green" variant="subtle" fontSize="xs" px={2}>
                          Showing {sites.length}
                        </Badge>
                      )}
                    </HStack>
                    </HStack>
                    
                    {/* Grid Layout Controls - positioned at top right - hidden on mobile */}
                    <ButtonGroup size="sm" isAttached variant="outline" display={{ base: "none", md: "flex" }}>
                      <IconButton
                        icon={<Icon as={Rectangle} boxSize={3} />}
                        aria-label="Single column layout"
                        onClick={() => setGridColumns(1)}
                        isActive={gridColumns === 1}
                        color="interactive.primary"
                        borderColor="border.default"
                        _hover={{ bg: 'bg.accent' }}
                        _active={{ bg: 'bg.surface' }}
                        minW="32px"
                        h="32px"
                      />
                      <IconButton
                        icon={<Icon as={Columns} boxSize={3} />}
                        aria-label="Two column layout"
                        onClick={() => setGridColumns(2)}
                        isActive={gridColumns === 2}
                        color="interactive.primary"
                        borderColor="border.default"
                        _hover={{ bg: 'bg.accent' }}
                        _active={{ bg: 'bg.surface' }}
                        minW="32px"
                        h="32px"
                      />
                      <IconButton
                        icon={<Icon as={SquaresFour} boxSize={3} />}
                        aria-label="Three column layout"
                        onClick={() => setGridColumns(3)}
                        isActive={gridColumns === 3}
                        color="interactive.primary"
                        borderColor="border.default"
                        _hover={{ bg: 'bg.accent' }}
                        _active={{ bg: 'bg.surface' }}
                        minW="32px"
                        h="32px"
                      />
                    </ButtonGroup>
                  </Flex>
                )}
                
                {/* Results */}
                {isLoading ? (
                  <SimpleGrid
                    columns={{
                      base: 1,
                      sm: gridColumns === 1 ? 1 : 2,
                      md: gridColumns
                    }}
                    spacing={6}
                  >
                    {skeletonCards}
                  </SimpleGrid>
                ) : sites.length > 0 ? (
                  <Fade in={!isLoading}>
                    <SimpleGrid
                    columns={{
                      base: 1,
                      sm: gridColumns === 1 ? 1 : 2,
                      md: gridColumns
                    }}
                    spacing={6}
                  >
                      {sites.map(site => (
                        <ElasticSiteCard
                          key={site.id}
                          site={site}
                          onMediaClick={(mediaItem, index) => handleMediaClick(site, mediaItem, index)}
                          showFilteredMedia={filters.features?.length > 0}
                          filteredMedia={getFilteredMedia(site.media || [])}
                          systemFilteredMedia={getFilteredMedia(site.media || [])}
                          onFeatureFilter={(feature) => handleFeatureToggle(feature)}
                          filters={{ ...filters, featuresMetadata, featureFilterLogic: filters.featureFilterLogic }} // Pass featureFilterLogic
                          searchScore={site._score}
                        />
                      ))}
                    </SimpleGrid>
                  </Fade>
                ) : (
                  <Fade in={!isLoading}>
                    <Box
                      textAlign="center"
                      py={{ base: 8, md: 16 }}
                      px={{ base: 4, md: 8 }}
                      bg="bg.surface"
                      borderRadius="xl"
                      boxShadow="0 2px 4px rgba(0,0,0,0.1)"
                      border="1px solid"
                      borderColor="border.default"
                      className="map-texture"
                    >
                      <Heading size={{ base: 'sm', md: 'md' }} mb={3} color="text.default">
                        No sites found matching your criteria
                      </Heading>
                      <Text color="text.muted" mb={6} fontSize={{ base: 'sm', md: 'md' }}>
                        Try adjusting your filters or searching for a different location
                      </Text>
                      <Button
                        color="interactive.primary"
                        borderColor="border.default"
                        variant="outline"
                        onClick={resetFilters}
                        leftIcon={<Icon as={Funnel} />}
                        _hover={{ bg: 'bg.accent' }}
                      >
                        Reset Filters
                      </Button>
                    </Box>
                  </Fade>
                )}
              </VStack>
            </GridItem>
          </Grid>
        </VStack>
      </Container>

      {/* Mobile FAB for Filters */}
      <IconButton
        aria-label="Open filters"
        icon={<Icon as={Funnel} boxSize={6} />}
        onClick={onFilterDrawerOpen}
        position="fixed"
        bottom={{ base: '1rem', sm: '2rem' }}
        right={{ base: '1rem', sm: '2rem' }}
        display={{ base: 'flex', md: 'none' }}
        size="lg"
        colorScheme="blue"
        isRound
        shadow="lg"
        zIndex="docked"
      >
        {hasActiveFilters && (
          <Badge
            position="absolute"
            top="-1px"
            right="-1px"
            colorScheme="red"
            variant="solid"
            borderRadius="full"
            px={1.5}
            py={0.5}
            fontSize="xs"
            lineHeight="1"
          >
            {activeFilterCount}
          </Badge>
        )}
      </IconButton>
      
      <ElasticImagePreviewModal
        isOpen={isOpen}
        onClose={onClose}
        imageUrl={selectedMedia?.url}
        onPrevious={() => handleNavigate('prev')}
        onNext={() => handleNavigate('next')}
        hasMultiple={selectedSiteMedia.length > 1}
        currentImage={selectedMedia}
        onFeaturesUpdated={(predictions) => {
          setSelectedMedia(prev => ({ ...prev, predictions }));
          const updatedMedia = [...selectedSiteMedia];
          const mediaIndex = selectedMediaIndex;
          if (mediaIndex >= 0 && mediaIndex < updatedMedia.length) {
            updatedMedia[mediaIndex] = { ...updatedMedia[mediaIndex], predictions };
          }
          setSelectedSiteMedia(updatedMedia);
        }}
      />

      {/* Mobile Filter Modal - Custom Implementation */}
      {isFilterDrawerOpen && (
        <>
          {/* Overlay */}
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            backdropFilter="blur(4px)"
            zIndex={1400}
            onClick={onFilterDrawerClose}
            className="mobile-filter-overlay"
          />
          
          {/* Bottom Sheet Content */}
          <Box
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            bg="bg.surface"
            borderRadius="20px 20px 0 0"
            maxH="85vh"
            zIndex={1500}
            boxShadow="0 -10px 30px rgba(0, 0, 0, 0.25)"
            transform={isFilterDrawerOpen ? "translateY(0)" : "translateY(100%)"}
            transition="transform 0.3s ease-out"
            className="mobile-filter-bottom-sheet"
            pb="env(safe-area-inset-bottom)"
          >
            {/* Drag Handle */}
            <Box
              w="40px"
              h="4px"
              bg="gray.300"
              borderRadius="2px"
              mx="auto"
              mt={3}
              mb={2}
              cursor="pointer"
              onClick={onFilterDrawerClose}
              _hover={{ bg: "gray.400" }}
            />
            
            {/* Header */}
            <Flex
              justify="space-between"
              align="center"
              px={6}
              py={4}
              borderBottomWidth="1px"
              borderColor="border.default"
            >
              <Text
                fontSize="lg"
                fontWeight="bold"
                color="text.default"
              >
                Filter Search Results
              </Text>
              <IconButton
                icon={<Icon as={X} />}
                variant="ghost"
                size="sm"
                onClick={onFilterDrawerClose}
                aria-label="Close filters"
                _hover={{ bg: 'bg.accent' }}
              />
            </Flex>
            
            {/* Content */}
            <Box
              px={6}
              py={4}
              overflowY="auto"
              maxH="calc(85vh - 100px)"
              className="mobile-filter-content"
            >
              <ElasticSearchFilters
                filters={{...filters, featuresMetadata}}
                onFilterChange={handleFilterChange}
                onReset={resetFilters}
                filterOptions={filterOptions}
                onInfoClick={onInfoModalOpen}
              />
            </Box>
          </Box>
        </>
      )}

      {/* Feature Filter Logic Info Modal */}
      <Modal isOpen={isInfoModalOpen} onClose={onInfoModalClose} isCentered motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
        <ModalContent
          bg={useColorModeValue('white', 'gray.800')}
          color={useColorModeValue('gray.800', 'whiteAlpha.900')}
          borderRadius="xl"
          border="1px solid"
          borderColor={useColorModeValue('gray.200', 'gray.700')}
        >
          <ModalHeader fontFamily="heading" fontWeight="bold" color={useColorModeValue('gray.700', 'white')}>Feature Filtering Logic</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color={useColorModeValue('gray.700', 'whiteAlpha.800')}>
                When you select multiple features to filter by, you can choose how they are combined:
              </Text>
              <Box p={3} borderWidth="1px" borderRadius="md" borderColor={useColorModeValue('gray.200', 'gray.600')} bg={useColorModeValue('gray.50', 'gray.700')}>
                <Heading size="sm" mb={1} color={useColorModeValue('blue.600', 'blue.300')}>AND Logic (Default)</Heading>
                <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')}>
                  Shows sites where media items contain <Text as="strong" color={useColorModeValue('gray.700', 'gray.100')}>ALL</Text> of the selected features. This is more restrictive and helps you find very specific results.
                </Text>
              </Box>
              <Box p={3} borderWidth="1px" borderRadius="md" borderColor={useColorModeValue('gray.200', 'gray.600')} bg={useColorModeValue('gray.50', 'gray.700')}>
                <Heading size="sm" mb={1} color={useColorModeValue('blue.600', 'blue.300')}>OR Logic</Heading>
                <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')}>
                  Shows sites where media items contain <Text as="strong" color={useColorModeValue('gray.700', 'gray.100')}>ANY</Text> of the selected features. This is less restrictive and broadens your search.
                </Text>
              </Box>
              <Text fontSize="xs" color="text.muted">
                This logic applies to the features found within individual photos, videos, or 3D models associated with a site.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              color="interactive.primary"
              borderColor="border.default"
              onClick={onInfoModalClose}
              _hover={{ bg: 'bg.accent' }}
            >
              Got it
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
